import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import useIsMobile from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import { normalizeFabricLabel } from '@/util/normalize-fabric-label';
import clsx from 'clsx';
import Link from 'next/link';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { SwatchDataValue } from '../types/data';
import type { ProductRoot } from '../types/product';
import { useGetInstockFabricOptions } from './use-get-instock-fabric-options';

type FabricOptionProps = {
  item: {
    uid: string;
    label: string;
    values: SwatchDataValue[];
  };
  createFabricButton?: React.ReactNode;
  handleClick: any;
  clickIndex: number;
  optionSpecs: any;
  product: ProductRoot;
  onOptionClick?: (colorLabel: string, fabricLabel: string, colorPrice: number | null) => void;
  hasOnlyOneColor: boolean;
  options: any[];
};

export const FabricOptions = ({
  item,
  createFabricButton,
  handleClick,
  clickIndex,
  optionSpecs,
  product,
  onOptionClick,
  hasOnlyOneColor,
  options,
}: FabricOptionProps) => {
  const form = useFormContext();
  const { watch, setValue, getValues } = form;

  const allFormData = watch();

  const { values } = useGetInstockFabricOptions({
    product,
    oldValues: item?.values,
  });

  const [showFabricDialog, setShowFabricDialog] = useState(false);
  const displayFabricLabel = allFormData[item.label]?.label?.replace('-default', '');
  const showFabricDetailsTrigger = ['satin charmeuse', 'crystal satin'].includes(displayFabricLabel?.toLowerCase());

  const findOtherFabricSameColor = (nextFabricName: string, prevColorLabel: string) => {
    if (!nextFabricName || hasOnlyOneColor) return null;

    const colorField = `Color-${normalizeFabricLabel(nextFabricName)}`;
    const colorOptions = options?.find(x => x?.label?.includes(colorField))?.values;

    if (!Array.isArray(colorOptions) || colorOptions.length === 0) {
      return null;
    }

    const sameColorOption = prevColorLabel
      ? colorOptions.find(option => option?.label === prevColorLabel) || colorOptions[0]
      : colorOptions[0];

    return sameColorOption || null;
  };

  return (
    <>
      <FormField
        key={item.uid}
        control={form.control}
        name={item.label}
        render={({ field }) => (
          <FormItem
            suppressHydrationWarning
            className='space-y-2.5 bg-white  px-2.5  py-[15px] lg_p-0 lg_px-0'
          >
            <div className={clsx('flex items-center', showFabricDetailsTrigger ? 'justify-between' : '')}>
              <FormLabel className='text-colorDefault flex items-center gap-x-[5px] '>
                <span className='text-base font-medium uppercase leading-5 lg_font-normal'>{item.label}: </span>
                <span className='text-[15px] leading-5'>{displayFabricLabel}</span>
              </FormLabel>
              {showFabricDetailsTrigger ? (
                <button
                  type='button'
                  className='text-[15px] leading-5 underline '
                  onClick={() => {
                    setShowFabricDialog(true);
                  }}
                >
                  Fabric Details
                </button>
              ) : null}
            </div>
            <FormMessage id={item.label} />
            <FormControl suppressHydrationWarning>
              <RadioGroup
                suppressHydrationWarning
                onValueChange={value => {
                  const selectedOption = values.find(v => v.value_index.toString() === value);

                  if (selectedOption) {
                    field.onChange({
                      value_index: value,
                      label: selectedOption?.label,
                      price: selectedOption?.price,
                      option_id: selectedOption?.option_id?.toString() || item?.attribute_id,
                    });

                    const sameColorOption = findOtherFabricSameColor(selectedOption.label, getValues('Color')?.label);

                    if (sameColorOption) {
                      setValue('Color', {
                        value_index: sameColorOption.value_index.toString(),
                        label: sameColorOption.label,
                        price: sameColorOption.price,
                        sku: sameColorOption.sku,
                        option_id: sameColorOption.option_id.toString(),
                      });
                    } else {
                      form.resetField('Color', { defaultValue: null });
                      setValue('Color', undefined);
                    }

                    form.resetField('Size', { defaultValue: null });
                    setValue('Size', undefined);

                    onOptionClick?.(
                      sameColorOption?.label || null,
                      selectedOption.label,
                      sameColorOption ? sameColorOption.price : null,
                    );
                  }

                  const couldSelect = optionSpecs.includes(value);

                  // 布料永远加入顶点
                  handleClick({
                    bool: true,
                    text: value,
                    index: clickIndex,
                    isFabric: true,
                  });

                  // fieldSize.onChange(undefined);

                  // fieldColor.onChange(undefined);
                }}
                value={field.value?.value_index}
                className='flex flex-wrap gap-2.5'
              >
                {values.map(option => (
                  <FabricRadioItem
                    key={option.uid}
                    item={option}
                    optionSpecs={optionSpecs}
                  />
                ))}
                {createFabricButton}
              </RadioGroup>
            </FormControl>
          </FormItem>
        )}
      />
      <FabricDialog
        open={showFabricDialog}
        onOpenChange={setShowFabricDialog}
        currentFabric={allFormData[item.label]?.label.replace('-default', '')}
      />
    </>
  );
};

const FabricRadioItem = ({ item, optionSpecs }: { item: SwatchDataValue; optionSpecs: any }) => {
  const inStock = optionSpecs.includes(item.value_index.toString());

  // const isOutOfStock = outOfStockVariants.flat().includes(item.value_index);
  const isOutOfStock = !inStock;

  return (
    <FormItem
      suppressHydrationWarning
      className='flex items-center space-y-0'
    >
      <FormControl suppressHydrationWarning>
        <RadioGroupItem
          value={item.value_index.toString()}
          className='peer sr-only'
          suppressHydrationWarning
        />
      </FormControl>
      <FormLabel
        suppressHydrationWarning
        className={cn(
          'flex min-h-[36px] w-full flex-1 cursor-pointer items-center justify-center rounded-[3px] border-1  border-solid border-80583F bg-f8f3ef px-2.5 text-center text-80583F peer-data-[state=checked]_bg-primary peer-data-[state=checked]_text-white  [&:has([data-state=checked])]_text-white ',
          // isOutOfStock && "opacity-50"
        )}
      >
        {item.label.replace('-default', '')}
      </FormLabel>
    </FormItem>
  );
};

const FabricDialog = ({
  open,
  onOpenChange,
  currentFabric,
}: {
  open: boolean;
  onOpenChange: (nextOpen: boolean) => void;
  currentFabric: string;
}) => {
  const isMobile = useIsMobile();

  if (!currentFabric || typeof currentFabric !== 'string') return null;

  const fabricConfig = fabricConfigs.find(x => x.defaultFabricLabel === currentFabric.toLowerCase());

  if (!fabricConfig) return null;

  const mainContent = (
    <>
      <div className=' flex max-h-[calc(100vh_-_96px)] flex-col pb-[68px]   supports-[height:100dvh]_max-h-[calc(100dvh_-_96px)] lg_gap-y-2.5 lg_px-10 lg_pb-10 lg_pt-[30px]'>
        <div className='flex flex-none items-center justify-end px-2.5 py-4 lg_px-0 lg_py-0'>
          <button
            type='button'
            onClick={() => {
              onOpenChange(false);
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='22'
              height='22'
              fill='none'
              viewBox='0 0 22 22'
            >
              <path
                stroke='#80583F'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='1.5'
                d='M19 19 3 3m16 0L3 19'
              ></path>
            </svg>
          </button>
        </div>

        <div className='flex min-h-0 flex-1 flex-col gap-y-5 overflow-auto px-2.5 pb-5 lg_flex-row lg_gap-x-[60px] lg_px-0 lg_pb-0'>
          <picture className='block h-auto w-full lg_w-auto lg_shrink lg_grow-0 lg_basis-[540px]'>
            <source
              media='(max-width: 959px)'
              srcSet={fabricConfig.mobileImageUrl}
              width={710}
              height={776}
            />
            <source
              media='(min-width: 960px)'
              srcSet={fabricConfig.desktopImageUrl}
              width={540}
              height={720}
            />
            <img
              alt={fabricConfig.title}
              className='h-auto w-full'
            />
          </picture>

          <div className='lg_shrink lg_grow-0 lg_basis-[640px]'>
            <div className='font-Shango text-[26px] leading-[1.2] lg_text-[30px]'>{fabricConfig.title}</div>
            <p className='mt-2.5 text-[15px] leading-[1.2] lg_mt-[30px] lg_text-[17px]'>{fabricConfig.desc}</p>

            {Array.isArray(fabricConfig.features) && fabricConfig.features.length > 0 ? (
              <>
                <div className='mt-2.5 text-[15px] font-medium leading-[1.4] lg_mt-[30px] lg_text-[17px]'>FEATURES</div>
                <ul className='mt-2.5 flex gap-x-3 lg_gap-x-5'>
                  {fabricConfig.features.map(({ icon, title }, i) => {
                    return (
                      <div
                        key={i}
                        className='flex basis-[107px] flex-col items-center gap-y-[9px] bg-f8f3ef py-2.5 lg_basis-[192px] lg_gap-y-4 lg_py-[19px]'
                      >
                        {icon}
                        <div className='flex max-w-[87px] flex-1 items-center text-center font-Shango text-[12px] leading-none text-80583F lg_max-w-[156px] lg_text-[22px]'>
                          {title}
                        </div>
                      </div>
                    );
                  })}
                </ul>
              </>
            ) : null}

            <div className='fixed bottom-0 left-0 flex w-full gap-x-2.5 px-4 py-2.5 shadow-[0_-5px_8px_0px_rgba(0,0,0,0.1)] lg_static lg_bottom-auto lg_left-auto lg_mt-10 lg_gap-x-10 lg_px-0 lg_py-0 lg_shadow-none'>
              <Link
                href={fabricConfig.shopDressHref}
                onClick={() => {
                  onOpenChange(false);
                }}
                className='flex h-12 flex-1 items-center justify-center rounded-[3px] border-1 border-solid border-80583F bg-80583F text-center text-[15px] font-medium leading-[1.4] text-white lg_min-w-[250px] lg_flex-none lg_hover_bg-transparent lg_hover_text-80583F'
              >
                SHOP DRESSES
              </Link>
              <Link
                href={fabricConfig.shopSwatchHref}
                onClick={() => {
                  onOpenChange(false);
                }}
                className='flex h-12 flex-1 items-center justify-center rounded-[3px] border-1  border-solid border-80583F text-center text-[15px] font-medium leading-[1.4] text-80583F lg_min-w-[250px] lg_flex-none lg_hover_bg-80583F lg_hover_text-white'
              >
                SHOP SWATCHES
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return isMobile ? (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      direction='bottom'
      handleOnly
    >
      <DrawerContent className='fixed bottom-0 left-0 z-[400]  w-full border-none bg-white outline-0 '>
        <DrawerTitle className='hidden'>Fabric Detail</DrawerTitle>
        {mainContent}
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className='block w-full max-w-[1320px] gap-y-0 border-none p-0 outline-0'
        showCloseButton={false}
      >
        <DialogTitle className='hidden'>Fabric Detail</DialogTitle>
        {mainContent}
      </DialogContent>
    </Dialog>
  );
};

const fabricConfigs = [
  {
    defaultFabricLabel: 'satin charmeuse',
    title: 'FABRIC: SATIN CHARMEUSE',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/fabric-description/fabric-satin-charmeuse-m.webp',
    desktopImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/fabric-description/fabric-satin-charmeuse.webp',
    desc: 'This elegant Satin Charmeuse fabric captures a delicate and sophisticated essence, ideal for special occasions and elegant outfits.',
    features: [
      {
        icon: (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='52'
            height='52'
            fill='none'
            viewBox='0 0 52 52'
            className='h-auto flex-none lg_w-[92px]'
          >
            <path
              fill='#E6DDD4'
              d='M7.891 30.043c4.53 1.23 7.667 2.483 9.913 4.726 2.246 2.244 3.508 5.384 4.731 9.902 1.232-4.525 2.486-7.658 4.732-9.902s5.39-3.504 9.913-4.726c-4.53-1.23-7.667-2.484-9.913-4.727s-3.508-5.384-4.732-9.902c-1.231 4.525-2.486 7.659-4.731 9.902-2.246 2.243-5.39 3.504-9.913 4.727'
            ></path>
            <path
              fill='#5D4F47'
              stroke='#5D4F47'
              strokeMiterlimit='10'
              strokeWidth='0.446'
              d='M22.542 46.724a.42.42 0 0 1-.402-.31c-1.332-5.213-2.564-8.694-4.918-11.046s-5.84-3.582-11.059-4.912a.41.41 0 0 1-.31-.402c0-.186.132-.356.31-.403 5.22-1.33 8.705-2.56 11.059-4.912s3.585-5.833 4.918-11.047a.41.41 0 0 1 .402-.31c.194 0 .356.132.403.31 1.332 5.214 2.563 8.695 4.918 11.047s5.839 3.582 11.058 4.912c.186.047.31.21.31.403a.42.42 0 0 1-.31.402c-5.22 1.33-8.704 2.56-11.058 4.912-2.355 2.352-3.586 5.833-4.918 11.047a.43.43 0 0 1-.403.31ZM7.89 30.046c4.53 1.23 7.667 2.483 9.913 4.727 2.246 2.243 3.508 5.384 4.732 9.901 1.23-4.525 2.485-7.658 4.731-9.901 2.246-2.244 5.39-3.505 9.913-4.727-4.53-1.23-7.667-2.483-9.913-4.727-2.246-2.243-3.508-5.384-4.731-9.902-1.232 4.526-2.486 7.659-4.732 9.902-2.246 2.244-5.39 3.505-9.913 4.727ZM38.494 18.379a.42.42 0 0 1-.403-.31c-1.1-4.293-1.905-5.105-6.21-6.204a.41.41 0 0 1-.31-.402c0-.186.131-.356.31-.402 4.297-1.099 5.11-1.911 6.21-6.205a.41.41 0 0 1 .403-.31c.185 0 .356.132.402.31 1.1 4.294 1.905 5.106 6.211 6.204.186.047.31.21.31.403a.42.42 0 0 1-.31.402c-4.298 1.098-5.111 1.903-6.21 6.204a.43.43 0 0 1-.403.31Zm-4.964-6.916c2.966.92 4.05 1.996 4.964 4.958.921-2.962 1.998-4.045 4.964-4.958-2.966-.92-4.05-1.996-4.964-4.959-.922 2.963-1.998 4.038-4.964 4.959Z'
            ></path>
          </svg>
        ),

        title: 'RELATIVELY SUBTLE SHEEN',
      },
      {
        icon: (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='52'
            height='52'
            fill='none'
            viewBox='0 0 52 52'
            className='h-auto flex-none lg_w-[92px]'
          >
            <path
              fill='#E7DED5'
              d='M3.697 18.875c1.789.74 3.665 1.325 5.583 1.743l1.329 1.949a.685.685 0 0 0 .953.178.69.69 0 0 0 .181-.952l-.556-.815q.546.088 1.098.157c.584.643 2.013 2.044 4.245 3.13a14.15 14.15 0 0 0 6.26 1.431c2.142 0 4.37-.46 6.633-1.373a16.6 16.6 0 0 0 4.835-2.979l.052-.046a16 16 0 0 0 1.59-1.634c1.395-1.643 2.52-3.603 3.345-5.819 1.396-3.76 1.447-6.939 1.447-7.072a.8.8 0 0 0-.03-.211 1.6 1.6 0 0 0-1.085-1.08c-.843-.254-2.047.016-3.788.855-1.257.606-2.786 1.51-4.547 2.687a96 96 0 0 0-5.507 4.013c-.66.071-2.786.35-5.269 1.154-1.736.563-3.286 1.265-4.606 2.09-1.57.981-2.82 2.139-3.722 3.446a34 34 0 0 1-1.352-.216l.835-1.19a.685.685 0 1 0-1.123-.786l-1.179 1.678a31.6 31.6 0 0 1-5.09-1.606.689.689 0 0 0-.532 1.268'
            ></path>
            <path
              fill='#5E5047'
              d='m50.176 28.808-17.94 13.353c-.2.126-2.39 1.495-5.287 2.083-3.984.809-7.329-.196-9.936-2.983-1.602-1.71-3.47-2.995-5.553-3.814-1.665-.656-3.467-1.016-5.356-1.072-3.21-.096-5.549.715-5.647.75a.683.683 0 1 0 .454 1.29c.022-.009 2.234-.77 5.2-.672 3.927.129 7.256 1.628 9.898 4.45 1.626 1.738 3.54 2.875 5.693 3.38.864.203 1.765.306 2.694.306.914 0 1.859-.098 2.823-.294 3.282-.667 5.682-2.225 5.782-2.29l.033-.025 17.958-13.366a.684.684 0 1 0-.816-1.096'
            ></path>
            <path
              fill='#5E5047'
              d='M.913 30.495c.022-.01 2.235-.77 5.2-.672 3.927.129 7.256 1.628 9.899 4.45 1.625 1.737 3.538 2.875 5.692 3.38.864.203 1.765.306 2.694.306.915 0 1.86-.098 2.824-.294 3.281-.667 5.681-2.225 5.782-2.29l.032-.025L50.99 21.988l.005-.002.008-.006.02-.015.007-.007.02-.017.006-.007q.01-.009.017-.017.004-.003.007-.01l.015-.017.009-.008.013-.016q.005-.006.009-.013l.01-.013q.006-.007.011-.015l.01-.013.01-.017.007-.011.01-.02q.003-.004.005-.009l.01-.021.005-.007.011-.024.002-.004q.006-.014.011-.027l.002-.004.01-.028.001-.005.009-.028v-.002l.007-.028v-.003l.006-.03v-.002l.004-.03v-.003q.004-.016.003-.033a.685.685 0 0 0-1-.65h-.002l-.026.014-.007.004-.02.014-.008.006c-.015.01-1.536.996-3.733 1.687-1.988.625-4.957 1.129-7.826-.011a.685.685 0 1 0-.506 1.273c1.307.52 2.723.782 4.215.782q.236 0 .476-.009a14.8 14.8 0 0 0 2.934-.416L32.235 34.24c-.202.13-2.424 1.511-5.328 2.093-3.966.793-7.295-.213-9.896-2.992-1.602-1.711-3.47-2.995-5.553-3.814-1.665-.656-3.467-1.016-5.356-1.072a18 18 0 0 0-2.535.104l3.994-2.972a.684.684 0 1 0-.82-1.099L.276 29.3a.69.69 0 0 0-.196.87.69.69 0 0 0 .833.324'
            ></path>
            <path
              fill='#5E5047'
              d='M3.697 18.875c1.789.74 3.665 1.325 5.583 1.743l1.329 1.949a.685.685 0 0 0 .953.178.69.69 0 0 0 .181-.952l-.556-.815q.546.088 1.098.157c.584.643 2.013 2.044 4.245 3.13a14.15 14.15 0 0 0 6.26 1.431c2.142 0 4.37-.46 6.633-1.373a16.6 16.6 0 0 0 4.835-2.979l.052-.046a16 16 0 0 0 1.59-1.634c1.395-1.643 2.52-3.603 3.345-5.819 1.396-3.76 1.447-6.939 1.447-7.072a.8.8 0 0 0-.03-.211 1.6 1.6 0 0 0-1.085-1.08c-.843-.254-2.047.016-3.788.855-1.257.606-2.786 1.51-4.547 2.687a96 96 0 0 0-5.507 4.013c-.66.071-2.786.35-5.269 1.154-1.736.563-3.286 1.265-4.606 2.09-1.57.981-2.82 2.139-3.722 3.446a34 34 0 0 1-1.352-.216l.835-1.19a.685.685 0 1 0-1.123-.786l-1.179 1.678a31.6 31.6 0 0 1-5.09-1.606.689.689 0 0 0-.532 1.268m17.155-3.367c1.63-.53 3.118-.822 4.083-.974-.12.4-.234.884-.288 1.418a.684.684 0 0 0 .613.75q.034.003.07.004a.685.685 0 0 0 .68-.616c.094-.924.404-1.67.53-1.949a93 93 0 0 1 5.455-3.977c5.295-3.541 6.829-3.476 7.174-3.371.07.021.111.048.14.096-.027.62-.201 3.39-1.373 6.522-1.036 2.772-2.524 5.041-4.444 6.785-.437.104-1.283-.061-1.938-.787a.685.685 0 1 0-1.019.918c.42.464.934.819 1.5 1.035a15.7 15.7 0 0 1-3.134 1.693c-4.23 1.706-8.173 1.709-11.72.004a13.3 13.3 0 0 1-2.684-1.715 32.6 32.6 0 0 0 4.76-.059c2.734-.242 5.298-.843 7.617-1.787 2.49-1.013 4.667-2.408 6.467-4.147a.684.684 0 1 0-.953-.983c-4.92 4.751-12.022 6.15-18.688 5.542 1.912-2.32 4.883-3.666 7.152-4.402'
            ></path>
          </svg>
        ),

        title: 'SILKY',
      },
      {
        icon: (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='52'
            height='52'
            fill='none'
            viewBox='0 0 52 52'
            className='h-auto flex-none lg_w-[92px]'
          >
            <path
              fill='#E7DED5'
              d='m43.957 7.96 6.468 8.435s-6.279 5.026-7.539 6.14c-1.239 1.116-2.667 1.431-4.788 1.683-2.121.253-5.86-.988-6.72-1.619-.861-.63-5.481-2.503-5.481-2.503l-.63-.378-7.54 4.522s-1.365-.042-3.444-.694-3.633-.716-3.633-.716L7.038 21.4s-.693.926-2.184 1.115c-1.491.19-4.452-1.367-4.452-1.367l11.424-9.212 11.256-3.68s5.86-.505 7.582.757c1.722 1.261 6.972 3.806 7.959 3.806s2.793-1.43 3.297-2.313c.546-.863 2.037-2.545 2.037-2.545'
            ></path>
            <path
              fill='#5E5047'
              d='M50.803 16.315a.61.61 0 0 0-.861-.105l-7.12 5.7c-1.868 1.493-4.262 2.145-6.572 1.808-2.059-.294-3.382-1.156-4.768-2.06l-.882-.569a.16.16 0 0 0-.084-.042l-8.715-3.512a3.44 3.44 0 0 1-1.428-3.933l13.378 4.08c.315.105.672-.084.777-.42.105-.316-.084-.674-.42-.779l-13.924-4.248a.614.614 0 0 0-.735.315 4.6 4.6 0 0 0-.44 2.63l-5.86 1.22c-.084.02-.168.041-.231.104L6.786 20.9l-1.008.715a3.13 3.13 0 0 1-3.948-.231l9.534-8.266a5.3 5.3 0 0 1 1.953-1.093c1.953-.631 5.65-1.81 8.652-2.777a11.5 11.5 0 0 1 8.4.484l8.086 3.68a.61.61 0 0 0 .672-.104l5.397-4.775a.626.626 0 0 0 .063-.862.623.623 0 0 0-.861-.063l-5.103 4.5-7.729-3.512a12.86 12.86 0 0 0-9.282-.525c-3.024.988-6.741 2.187-8.673 2.818a6.8 6.8 0 0 0-2.373 1.325L.549 20.879a.6.6 0 0 0-.084.841 4.35 4.35 0 0 0 3.486 1.725c.882 0 1.785-.273 2.562-.82l.63-.463a7.44 7.44 0 0 0 5.607 1.199c.042.042.063.084.126.126a6.04 6.04 0 0 0 3.738 1.304c1.05 0 2.08-.274 3.045-.82l5.943-3.576 4.431 1.788c.273.168.546.358.82.526 1.448.946 2.94 1.914 5.27 2.25.463.063.925.105 1.387.105a9.83 9.83 0 0 0 6.132-2.166l7.119-5.7a.7.7 0 0 0 .042-.883m-42.59 5.09 5.293-3.786 5.796-1.199a4.8 4.8 0 0 0 1.092 1.599l-5.901 3.407a6.21 6.21 0 0 1-6.28-.021m10.795 1.514c-1.512.862-3.318.862-4.788.021.294-.126.609-.273.903-.442l6.447-3.722 2.583 1.051zM50.551 36.608c-8.148-4.627-16.17-6.983-23.814-6.983-10.102 0-19.594 2.545-26.041 7.004a.61.61 0 0 0-.147.862.607.607 0 0 0 .861.147c6.216-4.311 15.456-6.793 25.327-6.793 7.434 0 15.246 2.292 23.205 6.814.105.063.21.084.294.084.21 0 .42-.105.546-.315.168-.274.063-.652-.23-.82'
            ></path>
            <path
              fill='#5E5047'
              d='M50.551 42.67c-8.148-4.627-16.17-6.983-23.814-6.983-10.102 0-19.594 2.545-26.041 7.004a.61.61 0 0 0-.147.862.607.607 0 0 0 .861.148c6.216-4.29 15.456-6.773 25.327-6.773 7.434 0 15.246 2.293 23.205 6.815.105.063.21.084.294.084a.62.62 0 0 0 .315-1.157'
            ></path>
          </svg>
        ),

        title: 'SOFT',
      },
    ],
    shopDressHref: '/fabrics/crystal-satin-vs-satin-charmeuse-comparison',
    shopSwatchHref: '/aw-satin-charmeuse-swatches-for-bridal-dresses-awsk210u2stcp.html',
  },
  {
    defaultFabricLabel: 'crystal satin',
    title: 'FABRIC: CRYSTAL SATIN',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/fabric-description/fabric-crystal-satin-m.webp',
    desktopImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/fabric-description/fabric-crystal-satin.webp',
    desc: 'Crystal Satin is a luxurious and sophisticated fabric with a delicate, elegant, and bright luster, perfect for formal occasions and elegant attire.',
    features: [
      {
        icon: (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='52'
            height='52'
            fill='none'
            viewBox='0 0 52 52'
            className='h-auto flex-none lg_w-[92px]'
          >
            <path
              fill='#E6DDD4'
              d='M13.045 35.677c4.173 1.166 7.063 2.354 9.131 4.482 2.07 2.127 3.232 5.105 4.36 9.39 1.134-4.292 2.29-7.263 4.358-9.39 2.07-2.128 4.965-3.323 9.132-4.483-4.174-1.166-7.063-2.354-9.132-4.482-2.069-2.127-3.231-5.105-4.359-9.39-1.134 4.292-2.29 7.263-4.359 9.39s-4.965 3.323-9.131 4.482'
            ></path>
            <path
              fill='#5D4F47'
              stroke='#5D4F47'
              strokeMiterlimit='10'
              strokeWidth='0.446'
              d='M26.537 51.5a.39.39 0 0 1-.371-.294c-1.227-4.944-2.361-8.245-4.53-10.475s-5.38-3.397-10.188-4.658a.384.384 0 0 1-.285-.382c0-.176.121-.337.285-.381 4.809-1.262 8.02-2.428 10.188-4.658s3.303-5.532 4.53-10.476a.38.38 0 0 1 .37-.293c.18 0 .329.125.372.293 1.227 4.945 2.361 8.246 4.53 10.476s5.379 3.396 10.187 4.658a.384.384 0 0 1 .286.381.4.4 0 0 1-.286.382c-4.808 1.261-8.018 2.428-10.187 4.658s-3.303 5.531-4.53 10.475a.38.38 0 0 1-.371.294Zm-13.49-15.816c4.173 1.166 7.062 2.355 9.131 4.482s3.232 5.106 4.359 9.39c1.134-4.292 2.29-7.263 4.359-9.39s4.965-3.323 9.131-4.482c-4.173-1.166-7.062-2.355-9.131-4.482s-3.232-5.106-4.36-9.39c-1.133 4.291-2.29 7.262-4.358 9.39-2.069 2.127-4.965 3.323-9.131 4.482ZM13.04 24.621a.39.39 0 0 1-.37-.293c-1.541-6.214-2.683-7.387-8.725-8.972a.384.384 0 0 1-.286-.381c0-.184.121-.338.286-.382 6.042-1.584 7.184-2.758 8.724-8.971a.38.38 0 0 1 .371-.294c.172 0 .329.125.371.294 1.541 6.213 2.683 7.387 8.725 8.971a.384.384 0 0 1 .286.382.39.39 0 0 1-.286.381c-6.042 1.585-7.184 2.758-8.725 8.972a.39.39 0 0 1-.37.293ZM5.5 14.975c4.701 1.393 6.185 2.912 7.533 7.746 1.356-4.834 2.833-6.36 7.534-7.746-4.701-1.394-6.185-2.913-7.534-7.747-1.348 4.834-2.825 6.353-7.533 7.747ZM41.234 24.616a.39.39 0 0 1-.37-.293c-1.014-4.072-1.756-4.842-5.722-5.883a.384.384 0 0 1-.286-.382c0-.176.122-.337.286-.381 3.96-1.042 4.708-1.812 5.721-5.884a.38.38 0 0 1 .371-.293c.172 0 .328.125.371.293 1.013 4.072 1.755 4.842 5.722 5.884a.384.384 0 0 1 .285.381.4.4 0 0 1-.285.382c-3.96 1.041-4.709 1.804-5.722 5.883a.385.385 0 0 1-.37.293Zm-4.573-6.558c2.733.873 3.731 1.893 4.573 4.702.85-2.81 1.84-3.836 4.573-4.702-2.732-.873-3.73-1.892-4.573-4.702-.842 2.81-1.833 3.83-4.573 4.702ZM28.07 8.53a.39.39 0 0 1-.372-.293c-.606-2.435-1.013-2.86-3.381-3.477a.384.384 0 0 1-.286-.381c0-.176.122-.338.286-.382 2.368-.623 2.782-1.042 3.381-3.477a.38.38 0 0 1 .371-.293c.178 0 .328.124.371.293.607 2.435 1.013 2.86 3.382 3.477a.384.384 0 0 1 .285.382.4.4 0 0 1-.285.381c-2.369.624-2.783 1.042-3.382 3.477a.39.39 0 0 1-.37.294ZM25.742 4.38c1.27.476 1.862 1.085 2.326 2.391.464-1.306 1.056-1.915 2.326-2.391-1.27-.477-1.862-1.086-2.326-2.392-.464 1.299-1.056 1.915-2.326 2.392Z'
            ></path>
          </svg>
        ),

        title: 'STRONG LUSTER',
      },
      {
        icon: (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='52'
            height='52'
            fill='none'
            viewBox='0 0 52 52'
            className='h-auto flex-none lg_w-[92px]'
          >
            <path
              fill='#E4DCD3'
              d='M15.531 41.24c-5.547 0-8.875-1.93-9.834-2.583l5.187-9.267c.78.743 2.848 2.287 6.626 2.287.99 0 2.069-.119 3.148-.327 3.148-.623 5.517-1.574 7.616-2.435 2.339-.95 4.377-1.782 6.746-1.782.33 0 .69.03 1.02.06 3.777.356 5.906 2.643 6.116 2.999.03.06.03.09.06.119l4.258 7.425c-1.47-.891-3.808-1.871-6.896-1.871-.54 0-1.08.03-1.65.089-2.908.327-5.756 1.307-8.785 2.346-3.687 1.278-7.525 2.584-11.873 2.881-.57.03-1.17.06-1.739.06'
            ></path>
            <path
              fill='#5E5047'
              d='M43.347 39.179c.31-.168.68-.046.863.245l.035.061v.002l3.478 7.04.003.004.002.005c.133.309.036.62-.212.825l-.01.008-.01.006c-.247.163-.608.182-.849-.057l-.02-.02-.058-.052a12.485 12.485 0 0 0-.998-.804c-.658-.475-1.551-1.026-2.527-1.358l-.005-.002-.005-.002-.14-.056-.417-.141c-1.02-.336-2.397-.691-4.826-.582-2.788.125-6.966.861-13.568 2.952-4.187 1.313-7.747 1.793-10.68 1.793-6.772 0-10.235-2.486-10.473-2.654-.154-.106-.232-.287-.267-.425a.56.56 0 0 1 .129-.518l2.758-3.622c.23-.314.64-.346.936-.137.321.228.356.64.141.937l-.003.005-2.33 3.03c1.912 1.025 8.421 3.784 19.385.305 6.756-2.142 11.095-2.898 14.034-3.018 2.95-.12 4.486.401 5.625.792l.005.001.005.003.142.056c.651.229 1.26.518 1.797.833l-2.251-4.564c-.182-.331-.023-.726.311-.891m-7.224-12.705c2.095.212 3.733.97 4.86 1.717.563.374 1 .747 1.304 1.05.151.15.272.285.358.396q.065.083.108.151.025.04.045.089l.008.01a.3.3 0 0 1 .038.08l4.393 7.667c.242.432-.1.897-.523.897a.8.8 0 0 1-.308-.082l-.012-.006-.01-.006c-1.352-.873-3.65-1.925-6.776-1.925-.501 0-1.033.028-1.564.087-3.134.354-6.27 1.509-9.657 2.66-3.378 1.148-6.992 2.285-11.04 2.57h-.006c-.627.029-1.203.059-1.779.059-5.858 0-9.318-2.06-10.286-2.74a.51.51 0 0 1-.258-.342.64.64 0 0 1 .06-.42l.003-.003.002-.004 5.247-9.416c.113-.214.357-.292.524-.292a.67.67 0 0 1 .428.183c.685.678 2.61 2.163 6.228 2.163.91 0 1.916-.116 3.047-.323l.607-.124c2.985-.64 5.258-1.61 7.327-2.45 2.202-.895 4.188-1.647 6.53-1.647.355 0 .682.03 1.063 0l.019-.001zM35.02 27.81c-2.228 0-4.167.782-6.543 1.735h.001c-2.1.862-4.516 1.85-7.717 2.478h-.003c-1.12.21-2.242.33-3.278.33-3.21 0-5.298-1.068-6.438-1.909l-4.476 7.994c1.31.78 4.322 2.142 8.935 2.142.561 0 1.124-.029 1.694-.058l.4-.03c4.12-.346 7.78-1.6 11.326-2.81 3.054-1.038 5.986-2.027 8.952-2.357.549-.06 1.127-.09 1.704-.09 2.09 0 3.835.432 5.236.98l-3.18-5.548-.007-.01q-.031-.067-.042-.103-.007-.02-.012-.03c-.149-.196-.703-.77-1.632-1.34a9.2 9.2 0 0 0-3.952-1.316c-.331-.03-.65-.058-.968-.058M25.546 3.188c.391 0 .703.306.703.698v8.257a.694.694 0 0 1-.703.698h-.028l-.318.01c-3.269.191-5.847 2.877-5.847 6.123 0 3.38 2.776 6.133 6.193 6.133s6.194-2.754 6.194-6.133v-.372h1.405v.372c0 4.164-3.4 7.53-7.599 7.53s-7.6-3.366-7.6-7.53c.001-3.911 3.014-7.138 6.897-7.483V3.886c0-.393.313-.699.703-.699'
            ></path>
          </svg>
        ),

        title: 'DURABLE',
      },
      {
        icon: (
          <svg
            width='52'
            height='52'
            viewBox='0 0 52 52'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='h-auto flex-none lg_w-[92px]'
          >
            <path
              d='M1.76611 23.5156H49.5045'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
            />
            <path
              d='M1.76611 27.4375H49.5045'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
            />
            <path
              d='M7.27588 4.89062V19.6391'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
            />
            <path
              d='M19.5024 4.89062V19.6391'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
            />
            <path
              d='M31.7686 4.89062V19.6391'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
            />
            <path
              d='M43.9976 4.89062V19.6391'
              stroke='#5E5047'
              stroke-width='1.11458'
              stroke-miterlimit='10'
            />
            <path
              d='M4.02148 43.0823L7.28167 46.3813V31.6328'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
            <path
              d='M10.5409 43.0823L7.28076 46.3813V31.6328'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
            <path
              d='M16.2446 43.0823L19.5048 46.3813V31.6328'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
            <path
              d='M22.7641 43.0823L19.5039 46.3813V31.6328'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
            <path
              d='M28.5063 43.0823L31.7665 46.3813V31.6328'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
            <path
              d='M35.0253 43.0823L31.7651 46.3813V31.6328'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
            <path
              d='M40.7739 43.0823L43.9953 46.3813V31.6328'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
            <path
              d='M47.2553 43.0823L43.9951 46.3813V31.6328'
              stroke='#5E5047'
              stroke-width='1.22604'
              stroke-miterlimit='10'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
          </svg>
        ),

        title: 'BEAUTIFUL DRAPE',
      },
    ],
    shopDressHref: '/fabrics/crystal-satin-vs-satin-charmeuse-comparison',
    shopSwatchHref: '/aw-crystal-satin-swatches-for-bridal-dresses-sk24110cscp.html',
  },
  {
    defaultFabricLabel: 'stretch satin',
    title: 'FABRIC: STRETCH SATIN',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/fabric-description/fabric-stretch-satin-m.webp',
    desktopImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/fabric-description/fabric-stretch-satin.webp',
    desc: 'Stretch Satin is a luxurious, versatile fabric with delicate shine, flexibility, and stretchability, perfect for formal and comfortable outfits.',
    features: [
      {
        icon: (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='52'
            height='52'
            fill='none'
            viewBox='0 0 52 52'
            className='h-auto flex-none lg_w-[92px]'
          >
            <path
              fill='#E6DDD4'
              d='M7.891 30.043c4.53 1.23 7.667 2.483 9.913 4.726 2.246 2.244 3.508 5.384 4.731 9.902 1.232-4.525 2.486-7.658 4.732-9.902s5.39-3.504 9.913-4.726c-4.53-1.23-7.667-2.484-9.913-4.727s-3.508-5.384-4.732-9.902c-1.231 4.525-2.486 7.659-4.731 9.902-2.246 2.243-5.39 3.504-9.913 4.727'
            ></path>
            <path
              fill='#5D4F47'
              stroke='#5D4F47'
              strokeMiterlimit='10'
              strokeWidth='0.446'
              d='M22.542 46.724a.42.42 0 0 1-.402-.31c-1.332-5.213-2.564-8.694-4.918-11.046s-5.84-3.582-11.059-4.912a.41.41 0 0 1-.31-.402c0-.186.132-.356.31-.403 5.22-1.33 8.705-2.56 11.059-4.912s3.585-5.833 4.918-11.047a.41.41 0 0 1 .402-.31c.194 0 .356.132.403.31 1.332 5.214 2.563 8.695 4.918 11.047s5.839 3.582 11.058 4.912c.186.047.31.21.31.403a.42.42 0 0 1-.31.402c-5.22 1.33-8.704 2.56-11.058 4.912-2.355 2.352-3.586 5.833-4.918 11.047a.43.43 0 0 1-.403.31ZM7.89 30.046c4.53 1.23 7.667 2.483 9.913 4.727 2.246 2.243 3.508 5.384 4.732 9.901 1.23-4.525 2.485-7.658 4.731-9.901 2.246-2.244 5.39-3.505 9.913-4.727-4.53-1.23-7.667-2.483-9.913-4.727-2.246-2.243-3.508-5.384-4.731-9.902-1.232 4.526-2.486 7.659-4.732 9.902-2.246 2.244-5.39 3.505-9.913 4.727ZM38.494 18.379a.42.42 0 0 1-.403-.31c-1.1-4.293-1.905-5.105-6.21-6.204a.41.41 0 0 1-.31-.402c0-.186.131-.356.31-.402 4.298-1.099 5.11-1.911 6.21-6.205a.41.41 0 0 1 .403-.31c.186 0 .356.132.403.31 1.1 4.294 1.905 5.106 6.21 6.204.187.047.31.21.31.403a.42.42 0 0 1-.31.402c-4.297 1.098-5.11 1.903-6.21 6.204a.43.43 0 0 1-.403.31Zm-4.964-6.916c2.966.92 4.05 1.996 4.964 4.958.922-2.962 1.998-4.045 4.964-4.958-2.966-.92-4.05-1.996-4.964-4.959-.922 2.963-1.998 4.038-4.964 4.959Z'
            ></path>
          </svg>
        ),
        title: 'Relatively Subtle Sheen',
      },
      {
        icon: (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='52'
            height='52'
            fill='none'
            viewBox='0 0 52 52'
            className='h-auto flex-none lg_w-[92px]'
          >
            <path
              fill='#E4DCD3'
              d='M43.133 42.892c-.052-.155-.103-.309-.137-.463-.942-3.268-1.626-6.588-1.985-9.976-.873-8.026-.24-15.915 2.02-23.666.187-.634.085-.702-.514-.514a59 59 0 0 1-10.047 2.02c-8.026.872-15.899.24-23.634-2.003-.206-.068-.462-.256-.634-.068-.188.188.018.428.086.633a61.3 61.3 0 0 1 2.328 14.717c.205 6.434-.565 12.748-2.362 18.926-.12.445-.257.753.445.547a59 59 0 0 1 11.039-2.139c5.767-.547 11.483-.308 17.182.77a59 59 0 0 1 5.733 1.37c.172.068.446.256.48-.154'
            ></path>
            <path
              fill='#5D4F47'
              d='M43.84 44.581c-.244-.076-.564-.152-.884-.259-9.17-2.82-18.462-3.398-27.892-1.661a66 66 0 0 0-7.16 1.768c-.365.106-.746.305-1.066-.046-.274-.305-.106-.67 0-1.006 2.24-7.147 3.154-14.462 2.605-21.944-.32-4.572-1.203-9.037-2.559-13.411-.122-.381-.366-.793-.015-1.143.35-.35.762-.107 1.142.015 8.135 2.515 16.437 3.307 24.892 2.256a59 59 0 0 0 10.389-2.286c.365-.107.746-.32 1.081-.03.38.335.152.746.03 1.127-2.193 6.995-3.092 14.157-2.604 21.487.304 4.603 1.127 9.114 2.513 13.503.092.32.214.624.244.96.03.457-.198.67-.716.67m-.838-1.813c-.046-.168-.107-.305-.137-.458a58 58 0 0 1-1.98-9.905c-.869-7.97-.229-15.803 2.01-23.514.183-.625.077-.686-.517-.519a57 57 0 0 1-9.978 2.012c-7.937.869-15.751.229-23.429-1.981-.213-.061-.457-.26-.64-.076-.183.183.03.426.076.64 1.356 4.785 2.148 9.646 2.316 14.614a59.9 59.9 0 0 1-2.346 18.806c-.122.442-.259.746.442.548a57 57 0 0 1 10.952-2.118c5.728-.549 11.41-.32 17.046.762 1.92.366 3.809.823 5.682 1.372.198.045.473.228.503-.183'
            ></path>
            <path
              fill='#5D4F47'
              d='M16.923 35.64c.533.137.974-.076 1.416-.076.381 0 .67.091.731.488.077.457-.228.655-.624.7-1.051.138-2.118.244-3.169.351-.502.046-.792-.198-.73-.716.121-1.052.273-2.103.395-3.17.046-.427.26-.686.686-.61.396.061.502.382.472.747-.046.442-.091.884.046 1.402A548 548 0 0 0 19.1 31.74c.152-.168.32-.32.487-.488.26-.259.564-.38.869-.091.32.305.243.64-.046.93-.96.975-1.935 1.935-2.894 2.91-.198.198-.381.396-.594.64M16.908 15.767c1.035 1.02 2.087 2.027 3.122 3.063.35.35.99.731.412 1.295-.549.534-.899-.137-1.234-.457-1.036-1.021-2.057-2.057-3.26-3.276.06.685.107 1.127.152 1.554.03.381-.091.686-.472.747-.427.06-.64-.198-.685-.625-.122-1.052-.275-2.103-.396-3.17-.046-.457.197-.747.67-.716 1.112.091 2.208.229 3.32.366.366.045.61.274.549.685-.061.35-.32.503-.67.488-.473-.03-.93-.091-1.402-.137-.046.076-.076.122-.106.183M35.065 16.586c-1.005 1.02-1.995 2.057-3.016 3.063-.335.32-.67.99-1.234.472-.594-.564.061-.945.396-1.295 1.02-1.037 2.072-2.058 3.123-3.079-.03-.06-.06-.106-.091-.167-.442.045-.884.106-1.31.137-.412.03-.716-.107-.747-.549-.03-.411.274-.594.625-.625 1.05-.121 2.117-.243 3.168-.35.503-.046.792.198.731.716-.106 1.036-.274 2.057-.38 3.094-.046.442-.229.762-.701.686-.427-.077-.503-.427-.457-.823.046-.381.076-.778.107-1.174a1.4 1.4 0 0 1-.214-.107M35.279 34.812a39 39 0 0 0-.122-1.448c-.03-.396.076-.731.533-.777.396-.03.579.228.625.61.122 1.05.274 2.118.396 3.17.045.456-.183.761-.655.716a60 60 0 0 1-3.321-.366c-.366-.046-.61-.29-.549-.686.061-.35.32-.503.67-.472.473.03.93.091 1.387.137.046-.061.091-.137.137-.198a353 353 0 0 1-2.407-2.286c-.38-.366-.761-.747-1.127-1.128-.29-.29-.366-.625-.03-.93.304-.29.609-.152.868.107l2.742 2.743c.228.214.472.427.853.808'
            ></path>
          </svg>
        ),
        title: 'Good Elasticity',
      },
      {
        icon: (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='52'
            height='52'
            fill='none'
            viewBox='0 0 52 52'
            className='h-auto flex-none lg_w-[92px]'
          >
            <path
              fill='#5E5047'
              d='M38.374 18.532c0-2.37-1.935-4.297-4.315-4.297-.25 0-.491.025-.728.064l-.62.103-.134-.611c-.727-3.327-3.705-5.811-7.28-5.811a7.424 7.424 0 0 0-7.251 5.758l-.117.512h-.733c-2.38 0-4.3 1.912-4.3 4.282s1.92 4.283 4.3 4.283c.4 0 .798-.062 1.195-.168l.41-.109.27.325a7.75 7.75 0 0 0 5.977 2.802 7.76 7.76 0 0 0 6.236-3.126l.336-.45.5.26c.578.3 1.236.466 1.954.466 2.38 0 4.3-1.913 4.3-4.283m1.32 0c0 3.097-2.511 5.598-5.62 5.598-.73 0-1.425-.133-2.064-.386a9.07 9.07 0 0 1-6.962 3.236 9.07 9.07 0 0 1-6.725-2.967 5.7 5.7 0 0 1-1.127.117c-3.109 0-5.62-2.501-5.62-5.598 0-2.993 2.347-5.431 5.313-5.59a8.745 8.745 0 0 1 8.408-6.278c3.995 0 7.36 2.64 8.422 6.267q.166-.01.34-.011c3.11 0 5.636 2.516 5.636 5.612'
            ></path>
            <path
              fill='#E7DED5'
              d='m43.958 44.086 6.468-8.434s-6.28-5.026-7.54-6.141c-1.239-1.115-2.667-1.43-4.788-1.683-2.12-.252-5.859.989-6.72 1.62-.86.63-5.48 2.503-5.48 2.503l-.63.378-7.54-4.522s-1.365.042-3.444.694c-2.08.652-3.633.715-3.633.715l-3.612 1.43s-.693-.925-2.185-1.114c-1.49-.19-4.452 1.367-4.452 1.367l11.425 9.212 11.256 3.68s5.86.505 7.581-.757 6.972-3.806 7.96-3.806c.986 0 2.792 1.43 3.297 2.313.546.862 2.037 2.545 2.037 2.545'
            ></path>
            <path
              fill='#5E5047'
              d='M50.803 35.732a.61.61 0 0 1-.861.105l-7.12-5.7c-1.868-1.493-4.262-2.145-6.572-1.808-2.059.294-3.382 1.156-4.768 2.06l-.882.569a.16.16 0 0 1-.084.042l-8.715 3.512a3.44 3.44 0 0 0-1.428 3.933l13.378-4.08c.315-.105.672.084.777.42.105.316-.084.673-.42.779l-13.924 4.248a.614.614 0 0 1-.735-.316 4.6 4.6 0 0 1-.44-2.629l-5.86-1.22a.47.47 0 0 1-.231-.105l-6.132-4.395-1.008-.715a3.13 3.13 0 0 0-3.948.231l9.534 8.266a5.3 5.3 0 0 0 1.953 1.093c1.953.631 5.65 1.809 8.652 2.776a11.5 11.5 0 0 0 8.4-.483l8.086-3.68a.61.61 0 0 1 .672.104l5.397 4.775c.252.23.273.61.063.862a.623.623 0 0 1-.861.063l-5.103-4.5-7.729 3.511a12.86 12.86 0 0 1-9.282.526c-3.024-.988-6.741-2.187-8.673-2.818a6.8 6.8 0 0 1-2.373-1.325L.549 31.168a.6.6 0 0 1-.084-.841 4.35 4.35 0 0 1 3.486-1.725c.882 0 1.785.273 2.562.82l.63.463a7.44 7.44 0 0 1 5.607-1.199c.042-.042.063-.084.126-.126a6.04 6.04 0 0 1 3.738-1.304c1.05 0 2.08.273 3.045.82l5.943 3.576 4.431-1.788c.273-.168.546-.358.82-.526 1.448-.946 2.94-1.914 5.27-2.25.463-.063.925-.105 1.387-.105 2.205 0 4.368.757 6.132 2.166l7.119 5.7c.21.23.252.61.042.883m-42.59-5.09 5.293 3.786 5.796 1.199a4.8 4.8 0 0 1 1.092-1.599l-5.901-3.407a6.21 6.21 0 0 0-6.28.021m10.795-1.514c-1.512-.863-3.318-.863-4.788-.021.294.126.609.273.903.441l6.447 3.723 2.583-1.052z'
            ></path>
          </svg>
        ),
        title: 'Softness',
      },
    ],
    shopDressHref: '/',
    shopSwatchHref: '/',
  },
  {
    defaultFabricLabel: 'sustainable satin',
    title: 'FABRIC: SUSTAINABLE SATIN',
    mobileImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/fabric-description/fabric-sustainable-satin-m.webp',
    desktopImageUrl: process.env.NEXT_PUBLIC_BASE_PATH + '/images/fabric-description/fabric-sustainable-satin.webp',
    desc: 'Sustainable Satin is an eco-friendly fabric made from sustainable materials and production processes, minimizing environmental impact while maintaining the luxurious feel and aesthetic appeal of traditional satin.',
    features: [
      {
        icon: (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='52'
            height='52'
            fill='none'
            viewBox='0 0 52 52'
            className='h-auto flex-none lg_w-[92px]'
          >
            <path
              fill='#5D4F47'
              d='M39.77 18.065c.11-.065.277-.146.46-.091.175.052.27.203.326.3 1.79 3.086 3.623 6.168 5.369 9.284.404.716.608 1.438.597 2.167s-.238 1.448-.66 2.163c-1.78 3.002-3.513 6.016-5.257 9.046l-.171.278c-.88 1.348-2.165 2.025-3.8 2.038l-1.074.013c-1.073.006-2.147-.004-3.226-.013l-.074.003v.062c.024.642 0 1.297 0 1.92.025.145.009.287-.001.443l-.057.908-.46-.785c-.21-.355-.376-.625-.538-.899V44.9c-1.222-2.093-2.428-4.183-3.648-6.263a.9.9 0 0 1-.144-.474 1 1 0 0 1 .14-.459l.004-.005c1.33-2.286 2.645-4.584 3.976-6.884l.002-.005.003-.003.027-.053c.013-.026.032-.066.057-.107a.63.63 0 0 1 .28-.25l.395-.181v.434c0 .773.013 1.536-.012 2.284v.065h.06c1.687-.011 3.373-.012 5.068-.012-1.854-3.202-3.696-6.403-5.561-9.592l-.001-.001a1.2 1.2 0 0 1-.104-.218.5.5 0 0 1-.021-.277c.044-.21.22-.33.363-.41 2.572-1.458 5.12-2.94 7.682-4.425M5.97 21.67l.047.004h8.078c.083 0 .207 0 .328.047q.202.079.315.287l2.058 3.556 2.066 3.558.004.008.004.007c.026.051.052.104.114.24l.343.753-.728-.39c-.322-.173-.556-.31-.788-.443l-.649-.367q-.33-.187-.657-.387c-.851 1.48-1.693 2.95-2.555 4.43h7.355c1.26 0 2.525.013 3.78 0h.002c.132 0 .325.01.47.138.153.137.175.336.175.493v.001q-.018 4.379 0 8.756v.001c0 .162-.017.368-.16.518-.146.153-.353.175-.52.175-3.583.012-7.177.06-10.768-.013l-.286-.013c-1.41-.101-2.512-.781-3.245-2.06-1.792-3.11-3.612-6.23-5.405-9.355l-.144-.27c-.668-1.356-.584-2.75.155-4.078.722-1.305 1.48-2.575 2.221-3.847-.579-.313-1.172-.678-1.735-.993l-.014-.007-.012-.009-.036-.02c-.017-.01-.053-.027-.084-.045a.53.53 0 0 1-.23-.244l-.09-.203.179-.132a.5.5 0 0 1 .312-.102c.04 0 .078.004.1.006m37.219 12.295a5.5 5.5 0 0 1-1.826.32c-3.16-.012-6.318 0-9.48 0-.13 0-.252 0-.349-.01a.55.55 0 0 1-.328-.137.5.5 0 0 1-.057-.067c-.788 1.372-1.57 2.738-2.362 4.103l.01.018.02.035.026.044 1.25 2.15 1.021 1.76a.4.4 0 0 1 .054-.063.52.52 0 0 1 .307-.123c.086-.009.195-.008.31-.008 1.625 0 3.245.012 4.854 0 1.198-.011 2.15-.482 2.765-1.507 1.273-2.16 2.523-4.34 3.784-6.515m-18.785.283c-3.697.012-7.396 0-11.103 0-.07 0-.092.011-.096.014-.002 0-.018.011-.045.064l-.004.006-.003.006a98 98 0 0 1-1.114 1.915c-.52.866-.745 1.774-.586 2.757l.03.166c.157.82.471 1.445.927 1.873.483.453 1.156.713 2.052.719 1.621.012 3.243.011 4.866.011h5.113l.001-.023-.006-1.863c-.005-1.865-.003-3.734.006-5.595l-.002-.048zM13.235 22.937c-1.367.042-2.745.03-4.116.026q.01.03.018.06c.039.21-.063.41-.147.554v.003a212 212 0 0 0-2.462 4.24v.001c-.585 1.018-.64 2.024-.125 3.034l.11.202h.002l1.665 2.878 1.666 2.877.37.636.014.024a5.7 5.7 0 0 1 .721-1.867c1.549-2.65 3.085-5.314 4.622-7.965l.002-.003c.075-.127.143-.242.205-.327a.6.6 0 0 1 .286-.233.5.5 0 0 1 .108-.022c-.694-1.194-1.42-2.391-2.081-3.62l-.001-.002c-.115-.218-.22-.333-.334-.399s-.27-.102-.523-.096m26.59-3.397-.006.003q-3.251 1.877-6.521 3.755c1.862 3.203 3.723 6.418 5.573 9.62.02.036.034.051.04.057q.006.002.041 0h1.438c.107.006.216.005.335.005q.175-.002.372.007l.176.006c.882.013 1.695-.2 2.406-.708l.002-.002c.753-.53 1.273-1.167 1.482-1.87.206-.695.118-1.494-.415-2.38v-.002c-.853-1.434-1.667-2.906-2.503-4.343v-.002q-.596-1.03-1.201-2.06-.605-1.029-1.2-2.059l-.018-.028zM20.577 5.814c3.512.037 7.01.011 10.525.011 1.718 0 3.036.748 3.926 2.175l.085.14.001.003c.529.917 1.068 1.835 1.598 2.755l.525.92.008.012q.003 0 .006-.002l.003-.002c.686-.415 1.378-.797 2.067-1.195l.256-.148.107.214.214.107-.148.256q-.294.512-.592 1.023t-.59 1.02l-.002.002c-1.013 1.74-2.037 3.478-3.05 5.229v.002a.83.83 0 0 1-.31.32.87.87 0 0 1-.43.097h-.004q-1.993-.017-3.988-.028l-3.99-.02h-.524l-.021-.256a.42.42 0 0 1 .195-.397.7.7 0 0 1 .117-.056l.03-.012.005-.002c.567-.33 1.137-.661 1.72-.982-.858-1.463-1.708-2.937-2.563-4.408a1521 1521 0 0 0-5.549 9.573v.002c-.084.142-.205.324-.415.376-.207.051-.4-.048-.54-.13h-.001a617 617 0 0 0-7.635-4.414l-.003-.002a1 1 0 0 1-.168-.115.5.5 0 0 1-.143-.206c-.065-.19.021-.367.09-.485 1.78-3.053 3.537-6.105 5.306-9.17V8.02c.423-.728.953-1.29 1.605-1.665q.979-.561 2.277-.542zm-.225 1.287a4 4 0 0 0-.486.028l-.148.026c-.847.187-1.435.698-1.868 1.46l-.001.001c-1.67 2.868-3.32 5.73-4.99 8.587l-.013.024.034.022a684 684 0 0 1 6.46 3.719l.032.015.019-.029v-.003q1.696-2.938 3.4-5.87t3.396-5.868c.42-.743.879-1.482 1.53-2.102q-1.715 0-3.43-.005-1.967-.005-3.934-.005m11.536.091c-1.582-.326-3.286.462-4.17 1.961v.001c-.194.325-.381.658-.57.992q-.283.503-.577 1.002c-.042.072-.046.112-.046.135.001.02.007.05.03.099l.029.055.001.002c1.024 1.762 2.05 3.527 3.063 5.29l.09.158q.041.075.072.14a.55.55 0 0 1 .05.34l-.015.054c1.545.011 3.091.023 4.637.045a.2.2 0 0 0 .042-.004l.006-.004a.1.1 0 0 0 .023-.023q.018-.025.048-.072c.474-.825.961-1.65 1.447-2.472.274-.496.561-.991.846-1.48a.4.4 0 0 1-.066-.01.48.48 0 0 1-.267-.197c-.048-.067-.099-.158-.15-.249l-1.227-2.121q-.614-1.062-1.235-2.12c-.476-.814-1.157-1.332-2.061-1.522'
            ></path>
          </svg>
        ),
        title: 'Eco-Friendly Materials',
      },
      {
        icon: (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='52'
            height='52'
            fill='none'
            viewBox='0 0 52 52'
            className='h-auto flex-none lg_w-[92px]'
          >
            <path
              fill='#E4DCD3'
              d='M15.53 41.249c-5.546 0-8.874-1.931-9.834-2.584l5.187-9.267c.78.742 2.849 2.287 6.627 2.287.989 0 2.068-.119 3.148-.327 3.148-.624 5.517-1.574 7.615-2.435 2.34-.95 4.378-1.782 6.747-1.782.33 0 .69.03 1.019.059 3.778.356 5.907 2.643 6.117 3 .03.06.03.089.06.119l4.257 7.425c-1.47-.891-3.808-1.871-6.896-1.871-.54 0-1.08.03-1.649.089-2.908.326-5.757 1.307-8.785 2.346-3.688 1.277-7.526 2.584-11.873 2.881-.57.03-1.17.06-1.74.06'
            ></path>
            <path
              fill='#5E5047'
              d='M43.346 39.179c.31-.169.679-.046.863.245l.034.061.001.002 3.478 7.04.003.004.002.005c.133.309.035.62-.212.825l-.01.008-.01.006c-.247.163-.608.182-.85-.057l-.019-.02-.058-.052a12.41 12.41 0 0 0-.998-.805c-.657-.475-1.552-1.026-2.527-1.358l-.005-.001-.005-.002-.14-.056-.417-.141c-1.02-.336-2.397-.691-4.827-.582-2.787.125-6.964.861-13.566 2.952-4.187 1.313-7.749 1.793-10.682 1.793-6.773 0-10.235-2.487-10.472-2.654-.154-.106-.232-.287-.267-.425a.56.56 0 0 1 .129-.518l2.758-3.622c.23-.314.639-.346.935-.137.322.228.356.64.142.937l-.003.005-2.33 3.03c1.911 1.025 8.421 3.784 19.386.305 6.755-2.142 11.093-2.898 14.033-3.018 2.95-.12 4.486.401 5.625.792l.005.001.005.003.141.056c.652.229 1.26.518 1.798.833l-2.25-4.564c-.182-.332-.025-.726.31-.891M36.12 26.474a10.74 10.74 0 0 1 4.861 1.717c.563.374 1 .747 1.303 1.05.152.15.272.285.359.396q.065.083.108.151a1 1 0 0 1 .046.089l.008.01c.009.013.026.042.038.08l4.391 7.667h.002c.242.432-.102.897-.524.897a.8.8 0 0 1-.309-.083l-.011-.005-.011-.006c-1.352-.873-3.65-1.925-6.776-1.925-.5 0-1.032.03-1.563.088-3.133.353-6.269 1.508-9.656 2.659-3.379 1.148-6.994 2.285-11.042 2.57h-.005c-.628.029-1.203.059-1.78.059-5.857 0-9.316-2.06-10.285-2.74a.51.51 0 0 1-.257-.342.64.64 0 0 1 .06-.42l.001-.004.002-.003 5.248-9.416c.113-.214.356-.292.523-.292a.671.671 0 0 1 .428.183c.685.678 2.61 2.163 6.23 2.163.91 0 1.915-.116 3.046-.323l.606-.124c2.985-.64 5.259-1.61 7.327-2.45 2.203-.895 4.189-1.647 6.53-1.647.355 0 .683.03 1.063 0l.02-.001zM35.02 27.81c-2.227 0-4.165.782-6.541 1.735l-1.642.662c-1.715.67-3.674 1.345-6.075 1.816h-.002c-1.12.21-2.244.33-3.28.33-3.209 0-5.298-1.068-6.437-1.909l-4.477 7.994c1.31.78 4.323 2.142 8.936 2.142.562 0 1.124-.029 1.693-.058l.4-.03c4.12-.346 7.78-1.6 11.327-2.81 3.054-1.038 5.986-2.027 8.952-2.357.548-.06 1.127-.09 1.704-.09 2.09 0 3.835.432 5.236.98l-3.18-5.548-.004-.005-.003-.005c-.023-.044-.033-.08-.041-.103l-.013-.03c-.148-.196-.703-.77-1.633-1.34a9.2 9.2 0 0 0-3.952-1.316c-.33-.03-.65-.058-.968-.058M25.546 3.188c.39 0 .703.305.703.698v8.257a.695.695 0 0 1-.703.698h-.028l-.319.01c-3.268.191-5.846 2.877-5.846 6.123 0 3.38 2.776 6.132 6.193 6.132s6.193-2.753 6.193-6.132v-.372h1.406v.372c0 4.164-3.4 7.529-7.6 7.529-4.198 0-7.598-3.365-7.598-7.53 0-3.91 3.014-7.137 6.897-7.482V3.886c0-.393.311-.698.702-.699'
            ></path>
          </svg>
        ),
        title: 'Durability',
      },
      {
        icon: (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='52'
            height='52'
            fill='none'
            viewBox='0 0 52 52'
            className='h-auto flex-none lg_w-[92px]'
          >
            <path
              fill='#5E5047'
              d='M33.585 2.664c.575-.007 1.02.166 1.33.485.306.318.464.766.493 1.285.071 1.203.015 2.398.015 3.915.172 2.077-.13 4.397-.729 6.682l-.124.457c-.091.338-.152.633-.115.907.036.265.168.523.488.783.627.504.717 1.388.291 2.025-.247.366-.291.665-.239.945.054.29.212.57.399.899 3.125 5.616 6.234 11.23 9.344 16.846l.001.002c.238.446.497.891.698 1.377v.001c.368.902.396 1.739.087 2.44-.308.7-.944 1.248-1.862 1.587-1.713.626-3.523.797-5.287.966-1.22.118-2.229.356-3.214 1.214l-.197.18c-3.85 3.641-9.8 3.368-13.474-.44v-.001c-.43-.452-.875-.694-1.434-.784l-.247-.029c-1.595-.135-3.208-.27-4.786-.597l-.674-.153c-1.572-.383-2.585-1.005-2.99-1.92-.405-.914-.182-2.078.6-3.49 3.195-5.771 6.39-11.527 9.599-17.283.172-.315.317-.576.364-.845.046-.259.001-.533-.232-.872-.47-.683-.346-1.581.32-2.125.28-.226.406-.454.447-.685.041-.236-.005-.49-.102-.772v-.002c-1.152-3.534-.91-7.198-.896-10.785 0-.692.159-1.236.475-1.612.318-.38.783-.571 1.358-.586h.002c.573-.008 1.035.158 1.344.532q.4.487.423 1.39v.266c-.022.612.002 1.142.184 1.583.18.434.52.796 1.16 1.062l.14.065c.321.166.586.421.82.706.132.162.258.337.378.513l.352.518v.002q.113.17.207.253a.3.3 0 0 0 .156.082c.06.006.145-.025.26-.15l.127-.16c.697-.993 1.502-1.886 2.659-2.42a.52.52 0 0 0 .285-.303c.046-.128.053-.278.06-.457l.003-.828q.006-.413.026-.836l.005-.195c.027-.447.15-.833.396-1.12.284-.33.718-.51 1.306-.518M23.265 20.52c-.133.046-.258.17-.395.423v.001c-3.393 6.138-6.828 12.26-10.192 18.41-.433.794-.523 1.39-.326 1.848.197.461.703.825 1.564 1.111h-.001c.888.286 1.803.44 2.73.56.922.119 1.866.205 2.793.353.505.076.817.045 1.029-.1s.353-.425.458-.928c1.145-5.361 2.36-10.709 3.562-16.055v-.001c.024-.101.04-.22.06-.351.021-.127.047-.263.09-.384a.63.63 0 0 1 .218-.313.53.53 0 0 1 .428-.067q.288.06.406.247a.64.64 0 0 1 .073.4c-.013.135-.05.278-.081.408a2 2 0 0 0-.07.36.5.5 0 0 1-.037.159c-.02.05-.03.073-.035.095v.003c-.902 4.042-1.795 8.092-2.71 12.132l-.924 4.036c-.089.396-.086.718.003 1 .09.283.27.538.557.797 1.706 1.535 3.673 2.355 5.962 2.355s4.255-.806 5.946-2.368l.2-.2c.179-.2.297-.4.362-.615.089-.286.088-.608-.001-.997-.891-3.852-1.753-7.703-2.616-11.568l-.576-2.55c-.19-.851-.378-1.706-.555-2.56v-.002c-.018-.089-.046-.183-.073-.299a1.5 1.5 0 0 1-.048-.346.54.54 0 0 1 .11-.342q.123-.154.382-.201a.53.53 0 0 1 .375.048c.1.058.166.15.212.247.044.095.074.204.097.305.025.106.043.198.067.284.128.455.214.912.312 1.374l1.67 7.532a2031 2031 0 0 0 1.68 7.52c.03.124.047.268.067.39.022.13.048.245.093.34a.42.42 0 0 0 .196.209c.094.046.232.07.44.048l.763-.088c1.782-.193 3.56-.312 5.269-.84l.334-.11c.735-.27 1.169-.607 1.347-1.027.204-.478.095-1.108-.374-1.96-1.669-3.041-3.348-6.071-5.028-9.1l-5.038-9.1c-.148-.267-.275-.428-.424-.5-.141-.068-.33-.067-.635.068-2.85 1.242-5.715 1.307-8.566.23l-.57-.23c-.268-.116-.445-.137-.58-.091m-.287-2.64a.23.23 0 0 0-.147.032.36.36 0 0 0-.126.172c-.08.193-.11.378-.07.532.037.147.143.284.377.38 1.776.737 3.488 1.54 5.518 1.36h.02c1.902.18 3.61-.65 5.388-1.388l.07-.033a.44.44 0 0 0 .237-.316c.028-.148-.003-.323-.057-.5l-.028-.07a.3.3 0 0 0-.089-.121.22.22 0 0 0-.14-.046c-.122-.001-.272.059-.44.132h.001c-3.351 1.58-6.708 1.61-10.075.029l-.002-.002c-.161-.08-.31-.152-.436-.161m2.498-9.612c-.783-.592-1.758-.61-2.58-.309a.41.41 0 0 0-.261.231.9.9 0 0 0-.041.391c.01.161.037.32.055.5.018.171.027.352-.008.518l-.001.004a.6.6 0 0 0-.007.166c.003.073.01.17.01.287v.01l-.11-.01.11.01c-.098 1.082.087 2.147.332 3.227.244 1.075.55 2.173.685 3.295a.46.46 0 0 0 .167.322c.092.078.21.132.35.197h-.001q4.3 1.883 8.557-.043l.165-.083c.15-.085.248-.18.282-.358.113-.679.253-1.355.394-2.028.142-.676.285-1.349.404-2.025.24-1.351.386-2.708.247-4.097v-.001c-.022-.226-.056-.37-.128-.463-.067-.087-.183-.15-.421-.17v-.001c-1.05-.07-1.983.032-2.753.8-.634.633-1.219 1.288-1.65 2.064-.126.229-.251.416-.38.548-.13.132-.272.22-.43.232-.16.012-.311-.057-.451-.184q-.209-.19-.417-.568h-.001a7.7 7.7 0 0 0-2.118-2.462m-2.078-4.369c-.195-.043-.354-.012-.47.065a.6.6 0 0 0-.244.4l-.001.002c-.032.141-.036.296-.03.462.004.16.019.346.019.516 0 .179-.021.388-.03.57-.01.19-.008.36.029.494.035.128.1.213.215.257.124.047.325.053.65-.033.22-.059.337-.149.4-.247.065-.1.085-.227.08-.381-.006-.156-.037-.32-.064-.497a1.7 1.7 0 0 1-.017-.52v-.002c.04-.238.045-.48-.03-.674-.07-.187-.218-.342-.508-.412m10.146.003c-.229.004-.37.061-.459.139a.57.57 0 0 0-.17.335 2.2 2.2 0 0 0-.02.49c.009.17.025.358.025.52 0 .157-.017.338-.023.493-.008.162-.005.308.028.43a.42.42 0 0 0 .197.27c.11.064.283.105.558.092l.168-.018a.55.55 0 0 0 .287-.118c.067-.062.095-.148.1-.266.004-.124-.016-.255-.029-.408a1.1 1.1 0 0 1 .03-.409 2 2 0 0 1-.034-.521c.006-.191.018-.369 0-.528-.017-.157-.06-.276-.148-.357s-.24-.143-.51-.143'
            ></path>
            <path
              fill='#E7DED5'
              d='M6.203 18.077a31 31 0 0 0 2.11 5.037l-.594 2.12a.64.64 0 0 0 .446.79.64.64 0 0 0 .79-.444l.247-.887q.266.445.548.879c-.108.804-.25 2.667.315 4.915a13.2 13.2 0 0 0 2.74 5.333c1.287 1.531 2.955 2.846 4.967 3.914a15.5 15.5 0 0 0 5.1 1.674q1.047.151 2.123.154c2.012.009 4.09-.364 6.169-1.107 3.525-1.262 5.827-3.137 5.922-3.217a.7.7 0 0 0 .133-.149c.28-.44.323-.947.12-1.423-.325-.755-1.242-1.453-2.888-2.193-1.188-.534-2.754-1.083-4.653-1.634a89 89 0 0 0-6.177-1.523 26 26 0 0 0-3.992-3.071c-1.446-.903-2.878-1.588-4.262-2.035-1.645-.533-3.223-.731-4.7-.59a32 32 0 0 1-.658-1.095l1.352-.118a.64.64 0 1 0-.113-1.276l-1.907.166a29.5 29.5 0 0 1-1.912-4.602.643.643 0 0 0-1.226.382'
            ></path>
            <path
              fill='#5E5047'
              d='M6.203 18.077a31 31 0 0 0 2.11 5.037l-.594 2.12a.64.64 0 0 0 .446.79.64.64 0 0 0 .79-.444l.247-.887q.266.445.548.879c-.108.804-.25 2.667.315 4.915a13.2 13.2 0 0 0 2.74 5.333c1.287 1.531 2.955 2.846 4.967 3.914a15.5 15.5 0 0 0 5.1 1.674q1.047.151 2.123.154c2.012.009 4.09-.364 6.169-1.107 3.525-1.262 5.827-3.137 5.922-3.217a.7.7 0 0 0 .133-.149c.28-.44.323-.947.12-1.423-.325-.755-1.242-1.453-2.888-2.193-1.188-.534-2.754-1.083-4.653-1.634a89 89 0 0 0-6.177-1.523 26 26 0 0 0-3.992-3.071c-1.446-.903-2.878-1.588-4.262-2.035-1.645-.533-3.223-.731-4.7-.59a32 32 0 0 1-.658-1.095l1.352-.118a.64.64 0 1 0-.113-1.276l-1.907.166a29.5 29.5 0 0 1-1.912-4.602.643.643 0 0 0-1.226.382M18.92 28.312a25.4 25.4 0 0 1 3.15 2.331 7 7 0 0 0-1.187.647.64.64 0 0 0-.167.889q.018.027.039.052a.64.64 0 0 0 .85.116 6 6 0 0 1 1.71-.792c.634.13 3.34.702 6.121 1.507 5.713 1.655 6.589 2.79 6.721 3.1.027.063.033.108.016.157-.46.355-2.544 1.895-5.486 2.94-2.604.926-5.12 1.226-7.52.902-.337-.249-.727-.953-.602-1.857a.64.64 0 1 0-1.268-.177c-.08.578-.024 1.16.161 1.693a14.7 14.7 0 0 1-3.093-1.22c-3.762-1.998-6.133-4.814-7.048-8.373a12.4 12.4 0 0 1-.387-2.949 30.4 30.4 0 0 0 2.903 3.367c1.816 1.808 3.787 3.278 5.855 4.368 2.22 1.17 4.526 1.888 6.85 2.128a.639.639 0 1 0 .13-1.272c-6.352-.66-11.62-4.893-15.192-10.022 2.807-.03 5.554 1.286 7.444 2.465'
            ></path>
          </svg>
        ),
        title: 'Comfort',
      },
    ],
    shopDressHref: '/',
    shopSwatchHref: '/',
  },
];
